import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, User, Phone, Mail, Car, RefreshCw, DollarSign, Search, XCircle, Pencil, Check, X, Copy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import { downloadExcel } from "@/lib/downloadExcel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATES = ["CA", "AZ", "OR", "WA", "TX", "NV", "UT", "NM", "CO", "OK", "FL", "GA", "SC", "NC", "VA", "MD", "DC", "DE", "PA", "NJ", "NY"];

const JobsList = () => {
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_state: {} });
  const [costByState, setCostByState] = useState({});
  const [totalCostPerJob, setTotalCostPerJob] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterMode, setFilterMode] = useState("today"); // "today", "yesterday", or "range"
  const [selectedState, setSelectedState] = useState("all"); // State filter
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchJobs();
  }, [selectedDate, startDate, endDate, filterMode]);

  useEffect(() => {
    fetchStats();
  }, [jobs]);

  const getPSTDate = (date = new Date()) => {
    return new Date(date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
  };

  const fmtDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getDateParams = () => {
    if (filterMode === "range" && startDate && endDate) {
      return { start_date: startDate, end_date: endDate };
    } else if (filterMode === "today") {
      const today = fmtDate(getPSTDate());
      return { start_date: today, end_date: today };
    } else if (filterMode === "yesterday") {
      const y = getPSTDate();
      y.setDate(y.getDate() - 1);
      return { start_date: fmtDate(y), end_date: fmtDate(y) };
    }
    return {};
  };

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = getDateParams();
      const qs = params.start_date ? `?start_date=${params.start_date}&end_date=${params.end_date}` : "";
      
      const response = await axios.get(`${API}/bookings${qs}`);
      setJobs(response.data);

      // Fetch ads cost-per-job for same date range
      if (params.start_date) {
        try {
          const adsRes = await axios.get(`${API}/google-ads/cost-per-job`, { params });
          const cpjMap = {};
          adsRes.data?.state_breakdown?.forEach(row => {
            if (row.cost_per_job) cpjMap[row.state] = row.cost_per_job;
          });
          setCostByState(cpjMap);
          setTotalCostPerJob(adsRes.data?.cost_per_job || null);
        } catch { setCostByState({}); setTotalCostPerJob(null); }
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Calculate stats from current jobs list (exclude cancelled)
      const activeJobs = jobs.filter(j => j.status !== "cancelled");
      const stateCounts = {};
      activeJobs.forEach(job => {
        const state = job.state || "Unknown";
        stateCounts[state] = (stateCounts[state] || 0) + 1;
      });
      
      setStats({
        total: activeJobs.length,
        by_state: stateCounts
      });
    } catch (error) {
      console.error("Error calculating stats:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const handleRefresh = () => {
    fetchJobs();
    toast.success("Refreshed!");
  };

  const [confirmingCancel, setConfirmingCancel] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [editForm, setEditForm] = useState({});

  const handleCancel = async (jobId) => {
    if (confirmingCancel !== jobId) {
      setConfirmingCancel(jobId);
      return;
    }
    try {
      await axios.patch(`${API}/bookings/${jobId}/cancel`);
      toast.success("Booking cancelled");
      setConfirmingCancel(null);
      fetchJobs();
    } catch {
      toast.error("Failed to cancel booking");
    }
  };

  const handleRebook = async (jobId) => {
    try {
      await axios.patch(`${API}/bookings/${jobId}/rebook`);
      toast.success("Job rebooked!");
      fetchJobs();
    } catch {
      toast.error("Failed to rebook");
    }
  };

  const copyJobInfo = (job) => {
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const [year, month, day] = dateString.split("-");
      return `${parseInt(month)}/${parseInt(day)}/${year}`;
    };

    const textToCopy = `NEW JOB BOOKING

Customer: ${job.name} ${job.last_name}
Phone: ${job.phone}
Email: ${job.email}${job.confirm_number ? `\nConfirm #: ${job.confirm_number}` : ''}

Address: ${job.address}${job.city ? `, ${job.city}` : ''}, ${job.state}${job.zip ? ` ${job.zip}` : ''}

Vehicle: ${job.vehicle_year || ''} ${job.vehicle_make || ''} ${job.vehicle_model || ''}
Type: ${job.refurb_or_new || ''}

Lead: ${job.lead || ''}
Source: ${job.source || ''}
Call Date: ${formatDate(job.call_date)}
Install Date: ${formatDate(job.install_date)}

Discount: $${job.discount || '0'}
Phone Price: $${job.phone_price || '0'}
Pay Plan: ${job.pay_plan || ''}${job.warranty ? `\nWarranty: ${job.warranty}${Number(job.warranty_price) > 0 ? ` ($${job.warranty_price})` : ' (Free)'}` : ''}${job.notes ? `\n\nNotes: ${job.notes}` : ''}`;

    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    toast.success("Copied!");
  };

  const startEdit = (job) => {
    setEditingJob(job.id);
    setEditForm({ ...job });
  };

  const cancelEdit = () => {
    setEditingJob(null);
    setEditForm({});
  };

  const saveEdit = async (jobId) => {
    try {
      await axios.patch(`${API}/bookings/${jobId}`, editForm);
      toast.success("Booking updated");
      setEditingJob(null);
      setEditForm({});
      fetchJobs();
    } catch {
      toast.error("Failed to update booking");
    }
  };

  // Filter jobs by state and search
  const filteredJobs = jobs.filter(job => {
    if (selectedState !== "all" && job.state !== selectedState) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const haystack = `${job.name} ${job.last_name} ${job.phone} ${job.email} ${job.city} ${job.address}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Button
                onClick={() => navigate("/h2g")}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 px-2 sm:px-3 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Home</span>
              </Button>
              <div className="border-l border-gray-300 h-6 hidden sm:block"></div>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
                  Jobs List
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">
                  View all booked appointments
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => {
                  const params = getDateParams();
                  const qs = params.start_date ? `?start_date=${params.start_date}&end_date=${params.end_date}` : "";
                  downloadExcel(`${API}/bookings/export${qs}`, "Hybrid2Go_Jobs.xlsx");
                }}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2"
                data-testid="export-jobs-button"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 sm:gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        
        {/* Stats by State */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-base sm:text-lg font-bold text-gray-900">
              Jobs by State
            </h2>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">
                Total: {stats.total}
              </div>
              {totalCostPerJob && (
                <div className="text-sm font-semibold text-orange-600" data-testid="total-cpj">
                  ${totalCostPerJob.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/job
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-10 gap-3">
            {STATES.map(state => {
              const count = stats.by_state?.[state] || 0;
              const cpj = costByState[state];
              return (
                <div 
                  key={state} 
                  className={`text-center p-2 sm:p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedState === state 
                      ? "bg-blue-100 border-2 border-blue-500" 
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedState(state)}
                  data-testid={`state-filter-${state}`}
                >
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {count}
                  </div>
                  <div className="text-xs sm:text-sm font-medium text-gray-600 mt-0.5">
                    {state}
                  </div>
                  {cpj ? (
                    <div className="text-[10px] sm:text-xs font-semibold text-orange-600 mt-0.5" data-testid={`cpj-${state}`}>
                      ${cpj.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/job
                    </div>
                  ) : count > 0 ? (
                    <div className="text-[10px] sm:text-xs text-gray-400 mt-0.5">—</div>
                  ) : null}
                </div>
              );
            })}
          </div>
          
          {selectedState !== "all" && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setSelectedState("all")}
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                Clear filter - Show all states
              </button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Filters</h3>
          
          {/* Search */}
          <div className="relative mb-3 sm:mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, email, city..."
              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
              data-testid="search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Filter Mode Toggle */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 sm:mb-4">
            <button
              onClick={() => setFilterMode("all")}
              className={`px-3 sm:px-4 py-2 rounded-lg border-2 font-medium transition-colors text-sm ${
                filterMode === "all"
                  ? "bg-blue-50 border-blue-500 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
              }`}
            >
              All Time
            </button>
            <button
              onClick={() => setFilterMode("today")}
              className={`px-3 sm:px-4 py-2 rounded-lg border-2 font-medium transition-colors text-sm ${
                filterMode === "today"
                  ? "bg-blue-50 border-blue-500 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setFilterMode("yesterday")}
              className={`px-3 sm:px-4 py-2 rounded-lg border-2 font-medium transition-colors text-sm ${
                filterMode === "yesterday"
                  ? "bg-blue-50 border-blue-500 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
              }`}
            >
              Yesterday
            </button>
            <button
              onClick={() => setFilterMode("range")}
              className={`px-3 sm:px-4 py-2 rounded-lg border-2 font-medium transition-colors text-sm ${
                filterMode === "range"
                  ? "bg-blue-50 border-blue-500 text-blue-700"
                  : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
              }`}
            >
              Select Date Range
            </button>
          </div>

          {/* Date Pickers */}
          {filterMode === "range" && (
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Jobs List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              {selectedState === "all" ? "All Jobs" : `Jobs in ${selectedState}`} ({filteredJobs.length})
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-500 mt-2">Loading jobs...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">
                {selectedState === "all" 
                  ? "No jobs found for selected date(s)" 
                  : `No jobs found in ${selectedState} for selected date(s)`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredJobs.map((job) => (
                <div
                  key={job.id}
                  className={`border-2 rounded-lg p-3 sm:p-6 transition-colors ${
                    job.status === "cancelled" 
                      ? "border-red-200 bg-red-50/50 opacity-70" 
                      : "border-gray-200 hover:border-blue-300"
                  }`}
                >
                  {editingJob === job.id ? (
                    /* EDIT MODE */
                    <div className="space-y-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-bold text-gray-900">Edit Booking</h3>
                        <div className="flex gap-2">
                          <button onClick={() => saveEdit(job.id)} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700" data-testid={`save-edit-${job.id}`}>
                            <Check className="w-3.5 h-3.5" /> Save
                          </button>
                          <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300">
                            <X className="w-3.5 h-3.5" /> Cancel
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[
                          { key: "name", label: "First Name" },
                          { key: "last_name", label: "Last Name" },
                          { key: "phone", label: "Phone" },
                          { key: "email", label: "Email" },
                          { key: "address", label: "Address" },
                          { key: "city", label: "City" },
                          { key: "state", label: "State" },
                          { key: "zip", label: "Zip" },
                          { key: "call_date", label: "Call Date", type: "date" },
                          { key: "install_date", label: "Install Date", type: "date" },
                          { key: "vehicle_year", label: "Year" },
                          { key: "vehicle_make", label: "Make" },
                          { key: "vehicle_model", label: "Model" },
                          { key: "discount", label: "Discount" },
                          { key: "phone_price", label: "Price" },
                          { key: "lead", label: "Lead" },
                          { key: "source", label: "Source" },
                          { key: "notes", label: "Notes" },
                        ].map(({ key, label, type }) => (
                          <div key={key}>
                            <label className="text-[10px] font-semibold text-gray-500 uppercase">{label}</label>
                            <input
                              type={type || "text"}
                              value={editForm[key] || ""}
                              onChange={(e) => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                              data-testid={`edit-${key}-${job.id}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    /* VIEW MODE */
                    <>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            {job.name} {job.last_name}
                            {job.status === "cancelled" && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold" data-testid={`cancelled-badge-${job.id}`}>
                                CANCELLED
                              </span>
                            )}
                            <button
                              onClick={() => copyJobInfo(job)}
                              className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md text-xs font-medium transition-colors flex items-center gap-1"
                              data-testid={`copy-job-${job.id}`}
                            >
                              <Copy className="w-3 h-3" /> Copy
                            </button>
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Install: {formatDate(job.install_date)}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.state}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                            {job.lead}
                          </span>
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            {job.source}
                          </span>
                          {job.status !== "cancelled" && (
                            <>
                              <button
                                onClick={() => startEdit(job)}
                                className="px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-full text-xs font-medium transition-colors"
                                data-testid={`edit-btn-${job.id}`}
                              >
                                <Pencil className="w-3 h-3 inline mr-1" />Edit
                              </button>
                              {confirmingCancel === job.id ? (
                                <div className="flex items-center gap-1">
                                  <button onClick={() => handleCancel(job.id)} className="px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-full text-xs font-medium transition-colors" data-testid={`confirm-cancel-btn-${job.id}`}>Confirm</button>
                                  <button onClick={() => setConfirmingCancel(null)} className="px-2 py-1 text-gray-500 hover:text-gray-700 text-xs font-medium">No</button>
                                </div>
                              ) : (
                                <button onClick={() => handleCancel(job.id)} className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-full text-xs font-medium transition-colors" data-testid={`cancel-btn-${job.id}`}>Cancel</button>
                              )}
                            </>
                          )}
                          {job.status === "cancelled" && (
                            <button
                              onClick={() => handleRebook(job.id)}
                              className="px-3 py-1 bg-green-50 text-green-700 hover:bg-green-100 rounded-full text-xs font-medium transition-colors"
                              data-testid={`rebook-btn-${job.id}`}
                            >
                              Rebook
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-gray-700 mb-2">
                            <Phone className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{job.phone}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-700 mb-2">
                            <Mail className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{job.email}</span>
                          </div>
                          <div className="flex items-start gap-2 text-gray-700">
                            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <span className="break-words">{job.address}, {job.city}, {job.state} {job.zip}</span>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-gray-700 mb-2">
                            <Car className="w-4 h-4" />
                            <span>{job.vehicle_year} {job.vehicle_make} {job.vehicle_model}</span>
                          </div>
                          <div className="text-gray-700 mb-1">
                            <span className="font-medium">Service:</span> {job.service}
                          </div>
                          <div className="text-gray-700 mb-1">
                            <span className="font-medium">Type:</span> {job.refurb_or_new}
                          </div>
                          <div className="text-gray-700 mb-1">
                            <span className="font-medium">Discount:</span> ${job.discount}
                          </div>
                          <div className="text-gray-700">
                            <span className="font-medium">Pay Plan:</span> {job.pay_plan}
                          </div>
                          {job.warranty && (
                            <div className="text-emerald-700 mt-1" data-testid={`job-warranty-${job.id}`}>
                              <span className="font-medium">Warranty:</span> {job.warranty}{Number(job.warranty_price) > 0 ? ` ($${job.warranty_price})` : " (Free)"}
                            </div>
                          )}
                        </div>
                      </div>

                      {job.notes && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-700">
                            <span className="font-medium">Notes:</span> {job.notes}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsList;
